/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The saga for set range month picker filter
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.2.0
 */

import { SagaIterator } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import { API_HOSTNAME, EXPENSES_ACCOUNT, EXPENSES_SUBSCRIBER, FILTER_AND_SORT } from '../../../modules/app/Api';
import { EExpensesAnalyzerField } from '../../../pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import {
    makeSelectorExpensesAnalyzerSelectedId,
    makeSelectorExpensesAnalyzerType,
} from '../../../pages/PageExpensesAnalyzer/pageExpensesAnalyzerSelectors';
import { EExpensesAnalyzerTypes } from '../../../pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerTypes';
import { filterToJson } from '../../../sagas/filterToJson';
import { requestSaga } from '../../../sagas/requestSaga';
import { sendTableFilterAndSortError, setRangeMonthFilterSuccess, setTableFilter } from '../actions/tableActions';
import { SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL } from '../constants/actionConstants';
import { makeSelectorTableFilter } from '../selectors/tableSelectors';
import { TableNameType, TSetRangeMonthFilterAction } from '../types/tableTypes';

export function* sendRangeMonthFilterSaga(action: TSetRangeMonthFilterAction): SagaIterator {
    try {
        const expensesAnalyzerType = yield select(makeSelectorExpensesAnalyzerType());
        const selectedFilters = yield select(makeSelectorTableFilter());
        const { valueFrom, valueTo, filterValue } = action.payload.filter;
        if (expensesAnalyzerType === EExpensesAnalyzerTypes.Account ||
            expensesAnalyzerType === EExpensesAnalyzerTypes.AccountSingle) {
            const selectedId = yield select(makeSelectorExpensesAnalyzerSelectedId());
            const URL = selectedId ?
                `${API_HOSTNAME}${EXPENSES_ACCOUNT}${FILTER_AND_SORT}` :
                `${API_HOSTNAME}${EXPENSES_SUBSCRIBER}${FILTER_AND_SORT}`;
            const json = filterToJson({
                filter: {
                    [EExpensesAnalyzerField.MONTH_RANGE]: {
                        valueFrom, valueTo,
                    },
                },
            });
            yield call(requestSaga, URL, 'POST', json);
        }
        yield put(setTableFilter({
            filter: {
                ...selectedFilters,
                [EExpensesAnalyzerField.MONTH_RANGE]: {
                    valueFrom, valueTo, filterValue,
                },
            },
            skipLoadList: action.payload.skipLoadList,
            tableName: TableNameType.expensesAnalyzer,
        }));
        yield take(SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL);
        yield put(setRangeMonthFilterSuccess());
    } catch (error: any) {
        yield put(sendTableFilterAndSortError({ error }));
    }
}
